import {PropertyCharacteristic} from "../../model/rate/RateConfig";
import {PaperTypeService} from "../service-data/paper-type.service";
import {PackageTypeService} from "../service-data/package-type.service";
import {FacilityService} from "../service-data/facility.service";
import {CARRIER_SERVICE_LEVELS} from "./carrier-service-levels";

/**
 * Resolve a propertyCharacteristic cell-key token to a human-friendly
 * display label + extended tooltip.
 *
 * Centralizes the lookup so the preview matrix, the group matrix, and
 * any future cell-key renderer all agree on what `paperType: "5"`,
 * `packageType: "11"`, and `carrierServiceLevel: "UPS_GROUND"` look
 * like to the user.
 *
 *  - `paperType` / `packageType`: looks up the dynamic catalog object
 *    by stringified id. Falls back to the raw id when no match (so
 *    historical / removed types still render).
 *  - `carrierServiceLevel`: looks up the static
 *    {@link CARRIER_SERVICE_LEVELS} catalog and formats
 *    `"<carrier> — <serviceLevel>"`.
 *  - Static enums (interior/binding/laminate) and numeric / derived
 *    characteristics (quantityBreak, numberUp): pass through the raw
 *    value; no extra tooltip text.
 *  - Wildcard `*` and collapsed `-`: pass through verbatim — the
 *    caller usually wants special UI treatment for these (e.g. the
 *    matrix renders `*` as "Any" and `-` as "—") so we do NOT
 *    relabel them here.
 *
 * The service references are accepted as optional so callers that
 * don't have them injected (e.g. unit tests, or a future textual
 * export) can still get a sensible fallback label.
 */
export interface CharacteristicValueLabel {
  /** Short text shown in the matrix axis / preview header. */
  label: string;
  /** Full description for the matTooltip (empty when none applies). */
  tooltip: string;
}

export function describeCharacteristicValue(
  c: PropertyCharacteristic,
  raw: string,
  paperTypeService?: PaperTypeService,
  packageTypeService?: PackageTypeService,
  facilityService?: FacilityService
): CharacteristicValueLabel {
  // Wildcard / collapsed pass through — callers handle special rendering.
  if (raw === '*' || raw === '-') { return {label: raw, tooltip: ''}; }
  if (!raw) { return {label: '—', tooltip: ''}; }

  if (c === 'packageType' && packageTypeService) {
    const id = Number(raw);
    const pt = (packageTypeService.objects || []).find(x => x.ID === id);
    if (!pt) { return {label: raw, tooltip: ''}; }
    return {
      label: pt.name || raw,
      tooltip: formatPackageTypeTooltip(pt, raw)
    };
  }
  if (c === 'paperType' && paperTypeService) {
    const id = Number(raw);
    const pt = (paperTypeService.objects || []).find(x => x.ID === id);
    if (!pt) { return {label: raw, tooltip: ''}; }
    const parts: string[] = [`#${pt.ID}`];
    if ((pt as any).name) { parts.push((pt as any).name); }
    if ((pt as any).color) { parts.push(`color: ${(pt as any).color}`); }
    if ((pt as any).weight) { parts.push(`${(pt as any).weight}#`); }
    return {
      label: (pt as any).name || raw,
      tooltip: parts.join(' • ')
    };
  }
  if (c === 'carrierServiceLevel') {
    const def = CARRIER_SERVICE_LEVELS.find(x => x.ID === raw);
    if (!def) { return {label: raw, tooltip: ''}; }
    return {
      label: `${def.carrier} — ${def.serviceLevel}`,
      tooltip: def.brandedPackagingRequired ? `${def.ID} (branded packaging required)` : def.ID
    };
  }
  if (c === 'facility' && facilityService) {
    // Resolve the stringified Facility.ID to the facility's friendly
    // name (falling back to `name`, then to `#<id>`). Tooltip shows
    // the same name plus the id and city/state when available — same
    // pattern as PackageType / PaperType.
    const id = Number(raw);
    const f = (facilityService.objects || []).find(x => x.ID === id);
    if (!f) { return {label: raw, tooltip: ''}; }
    const display = (f as any).friendlyName || (f as any).name || ('#' + id);
    const tipParts: string[] = [`#${f.ID}`];
    if ((f as any).friendlyName) {
      tipParts.push((f as any).friendlyName);
    } else if ((f as any).name) {
      tipParts.push((f as any).name);
    }
    const cityState: string[] = [];
    if ((f as any).city)  { cityState.push((f as any).city); }
    if ((f as any).state) { cityState.push((f as any).state); }
    if (cityState.length > 0) { tipParts.push(cityState.join(', ')); }
    return {label: display, tooltip: tipParts.join(' • ')};
  }
  // interiorType / bindingType / laminateType / quantityBreak / numberUp:
  // values are already self-describing.
  return {label: raw, tooltip: ''};
}

/**
 * Build the multi-line tooltip for a {@link PackageType} cell value.
 * Includes name, carrier, service level (mirrors the carrier-service
 * vocabulary used by `serviceLevel` elsewhere), and the physical
 * dimensions in the order the user asked for (height, width, depth).
 *
 * Lines are joined with `\n` so the standard `matTooltip` renders
 * them stacked.
 */
function formatPackageTypeTooltip(pt: any, fallbackId: string): string {
  const lines: string[] = [];
  lines.push(`#${pt.ID}${pt.name ? ' — ' + pt.name : ''}`);
  const carrierBits: string[] = [];
  if (pt.carrier) { carrierBits.push(pt.carrier); }
  // PackageType has a `service` field (NOT `serviceLevel`); we expose
  // it as "service level" in the user-facing tooltip because that's
  // the same vocabulary used in carrier-service-levels.ts and the
  // ecommerce-connection UI.
  if (pt.service) { carrierBits.push(pt.service); }
  if (carrierBits.length > 0) { lines.push(carrierBits.join(' — ')); }
  // Dimensions. Skip zero values — they're "not configured" sentinels
  // for unrelated metadata fields, not real 0-inch dimensions.
  const dims: string[] = [];
  if (typeof pt.height === 'number' && pt.height > 0) { dims.push(`H ${pt.height}`); }
  if (typeof pt.width === 'number' && pt.width > 0)   { dims.push(`W ${pt.width}`); }
  if (typeof pt.depth === 'number' && pt.depth > 0)   { dims.push(`D ${pt.depth}`); }
  if (dims.length > 0) { lines.push(dims.join(' × ')); }
  if (lines.length === 1 && !pt.name) { return fallbackId; }
  return lines.join('\n');
}
